/* Shopify owns prices, stock, customer data and checkout. No local cart database. */
(() => {
  "use strict";
  const configNode = document.getElementById("VaultConfig");
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const money = (cents) =>
    new Intl.NumberFormat(config.locale || "en", {
      style: "currency",
      currency: config.currency || "EUR",
    }).format(cents / 100);
  let cartBusy = false;
  let opener = null;
  const drawer = () => document.getElementById("CartDrawer");
  const announce = (text) => {
    const status = document.getElementById("CartStatus");
    if (status) status.textContent = text;
  };
  async function request(url, options = {}) {
    const response = await fetch(url, {
      credentials: "same-origin",
      ...options,
    });
    let data;
    try {
      data = await response.json();
    } catch {
      throw new Error(
        "The store returned an unexpected response. Please try again.",
      );
    }
    if (!response.ok)
      throw new Error(
        data.description ||
          data.message ||
          "The request could not be completed.",
      );
    return data;
  }
  function showDrawer() {
    const dialog = drawer();
    if (!dialog || !config.drawer || !dialog.showModal) {
      window.location.assign(config.cart);
      return;
    }
    if (!dialog.open) {
      opener = document.activeElement;
      dialog.showModal();
    }
  }
  function closeDrawer() {
    drawer()?.close();
    if (opener?.isConnected) opener.focus();
  }
  async function refreshCart() {
    const url = new URL(config.root, location.origin);
    const pageContainer = document.querySelector("[data-cart-page]");
    const pageSection = pageContainer
      ?.closest(".shopify-section")
      ?.id.replace("shopify-section-", "");
    const sectionIds = ["cart-drawer", ...(pageSection ? [pageSection] : [])];
    url.searchParams.set("sections", sectionIds.join(","));
    const [sections, cart] = await Promise.all([
      request(url.toString()),
      request(`${config.cart}.js`),
    ]);
    const parser = new DOMParser();
    if (!sections["cart-drawer"])
      throw new Error(
        "Your cart was updated, but the drawer could not refresh. Open the full cart to review it.",
      );
    const newBody = parser
      .parseFromString(sections["cart-drawer"], "text/html")
      .querySelector("[data-drawer-body]");
    if (!newBody)
      throw new Error("Please open the full cart to review your changes.");
    drawer()
      ?.querySelector("[data-drawer-body]")
      ?.replaceChildren(...newBody.childNodes);
    if (pageSection && sections[pageSection]) {
      const nextPage = parser
        .parseFromString(sections[pageSection], "text/html")
        .querySelector("[data-cart-page]");
      if (nextPage) pageContainer.replaceChildren(...nextPage.childNodes);
    }
    document.querySelectorAll("[data-cart-count]").forEach((node) => {
      node.textContent = cart.item_count;
    });
    announce(`Cart updated. ${cart.item_count} items.`);
    return cart;
  }
  function setCartBusy(busy) {
    cartBusy = busy;
    const dialog = drawer();
    dialog?.setAttribute("aria-busy", String(busy));
    dialog
      ?.querySelectorAll('input,button[type="submit"],button[name="checkout"]')
      .forEach((input) => {
        input.disabled = busy;
      });
  }
  async function changeLine(key, quantity) {
    if (cartBusy) return;
    const error = drawer()?.querySelector("[data-drawer-error]");
    if (!Number.isInteger(quantity) || quantity < 0) {
      if (error) error.textContent = "Enter a whole number of zero or more.";
      return;
    }
    if (error) error.textContent = "";
    setCartBusy(true);
    try {
      await request(`${config.change}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity }),
      });
      await refreshCart();
      drawer()?.querySelector("[data-close-cart]")?.focus();
    } catch (failure) {
      if (error)
        error.textContent = `${failure.message} Review the full cart before checkout.`;
    } finally {
      setCartBusy(false);
    }
  }
  function selectMedia(section, id) {
    const panels = [...section.querySelectorAll("[data-media-id]")];
    if (!panels.some((panel) => panel.dataset.mediaId === String(id))) return;
    panels.forEach((panel) => {
      const selected = panel.dataset.mediaId === String(id);
      panel.hidden = !selected;
      if (!selected) {
        panel.querySelectorAll("video").forEach((video) => video.pause());
        panel
          .querySelectorAll("model-viewer")
          .forEach((model) => model.pause?.());
        // Reload only an iframe being hidden; prevents sound playing behind the next media.
        panel.querySelectorAll("iframe").forEach((frame) => {
          if (frame.dataset.played === "true") frame.src = frame.src;
        });
      } else
        panel.querySelectorAll("iframe").forEach((frame) => {
          frame.dataset.played = "true";
        });
    });
    section.querySelectorAll("[data-media-target]").forEach((button) => {
      button.setAttribute(
        "aria-pressed",
        String(button.dataset.mediaTarget === String(id)),
      );
    });
  }
  function initProduct(section) {
    if (section.dataset.initialized) return;
    section.dataset.initialized = "true";
    const variants = JSON.parse(
      section.querySelector("[data-variants]").textContent,
    );
    const commerce = JSON.parse(
      section.querySelector("[data-variant-commerce]").textContent,
    );
    const form = section.querySelector("[data-product-form]");
    const idSelect = form.querySelector("[data-variant-id]");
    const planSelect = form.querySelector("[data-selling-plan]");
    const button = form.querySelector("[data-add-button]");
    const fields = [...form.querySelectorAll("[data-option-position]")];
    section.querySelector(".variant-fallback").hidden = true;
    section.querySelector(".enhanced-options").hidden = false;
    function update(changeUrl = false) {
      const options = fields.map(
        (field) => field.querySelector("input:checked")?.value,
      );
      const variant = fields.length
        ? variants.find((item) =>
            item.options.every((value, i) => value === options[i]),
          )
        : variants.find((item) => String(item.id) === idSelect.value);
      const terms = variant ? commerce[String(variant.id)] : null;
      const allocations = terms?.plans || [];
      idSelect.value = variant ? String(variant.id) : "";
      let allocation;
      let planValid = true;
      if (planSelect) {
        [...planSelect.options].forEach((option) => {
          option.disabled = option.value
            ? !allocations.some(
                (item) =>
                  String(item.selling_plan_id ?? item.selling_plan?.id) ===
                  option.value,
              )
            : !!terms?.requiresPlan;
          option.hidden = option.disabled;
        });
        if (
          planSelect.selectedIndex < 0 ||
          planSelect.options[planSelect.selectedIndex]?.disabled
        ) {
          planSelect.value =
            [...planSelect.options].find((option) => !option.disabled)?.value ??
            "";
        }
        allocation = allocations.find(
          (item) =>
            String(item.selling_plan_id ?? item.selling_plan?.id) ===
            planSelect.value,
        );
        planValid = !terms?.requiresPlan || !!allocation;
        planSelect.required = !!terms?.requiresPlan;
        const planPrice = section.querySelector("[data-plan-price]");
        planPrice.textContent = allocation
          ? `Selected plan: ${money(allocation.price)}. Review the plan terms at checkout.`
          : "";
      } else if (terms?.requiresPlan) planValid = false;
      const price = allocation?.price ?? variant?.price;
      section.querySelector("[data-price]").textContent =
        price !== undefined ? money(price) : "—";
      const compare = allocation?.compare_at_price ?? variant?.compare_at_price;
      const compareNode = section.querySelector("[data-compare-price]");
      compareNode.hidden = !(compare > price);
      compareNode.textContent = compare ? money(compare) : "";
      section.querySelector("[data-availability]").textContent = !variant
        ? "Unavailable combination"
        : variant.available
          ? "Available"
          : "Sold out";
      button.disabled = !variant?.available || !planValid;
      button.textContent = !variant
        ? "Unavailable combination"
        : !variant.available
          ? "Sold out"
          : !planValid
            ? "Purchase plan unavailable"
            : form.dataset.preorder === "true"
              ? "Add pre-order to cart"
              : "Add to vault";
      if (variant) {
        const quantity = form.elements.quantity;
        const rules = terms?.quantityRule || {};
        quantity.min = rules.min || 1;
        quantity.step = rules.increment || 1;
        if (rules.max) quantity.max = rules.max;
        else quantity.removeAttribute("max");
        if (!quantity.checkValidity()) quantity.value = quantity.min;
        if (variant.featured_media?.id)
          selectMedia(section, variant.featured_media.id);
        else if (variant.featured_image?.id) {
          // Shopify's variant JSON commonly exposes featured_image instead of featured_media.
          const image = section.querySelector(
            `[data-image-id="${variant.featured_image.id}"]`,
          );
          if (image) selectMedia(section, image.dataset.mediaId);
        }
        if (changeUrl) {
          const url = new URL(location.href);
          url.searchParams.set("variant", variant.id);
          if (planSelect?.value)
            url.searchParams.set("selling_plan", planSelect.value);
          else url.searchParams.delete("selling_plan");
          history.replaceState({}, "", url);
        }
      }
    }
    const requestedPlan = new URL(location.href).searchParams.get(
      "selling_plan",
    );
    if (planSelect && requestedPlan) planSelect.value = requestedPlan;
    form.addEventListener("change", (event) => {
      if (event.target.matches('input[type="radio"], [data-selling-plan]'))
        update(true);
    });
    form.addEventListener("vault:variant-sync", () => update());
    update();
  }
  let modelLoading = false;
  function initModels(root = document) {
    const models = [
      ...root.querySelectorAll("model-viewer:not([data-vault-ready])"),
    ];
    if (!models.length || !window.Shopify?.loadFeatures || modelLoading) return;
    modelLoading = true;
    if (!document.getElementById("ModelViewerStyles")) {
      const link = document.createElement("link");
      link.id = "ModelViewerStyles";
      link.rel = "stylesheet";
      link.href =
        "https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css";
      document.head.append(link);
    }
    window.Shopify.loadFeatures([
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: (error) => {
          modelLoading = false;
          if (error) {
            console.error("Shopify 3D media could not load.", error);
            return;
          }
          document
            .querySelectorAll("model-viewer:not([data-vault-ready])")
            .forEach((model) => {
              model.dataset.vaultReady = "true";
              model.vaultUI = new window.Shopify.ModelViewerUI(model);
            });
        },
      },
    ]);
  }
  function initAddresses(root) {
    root.querySelectorAll("[data-country]").forEach((country) => {
      if (country.dataset.initialized) return;
      country.dataset.initialized = "true";
      const province = country.form.querySelector("[data-province]");
      const container = country.form.querySelector("[data-province-container]");
      if (country.dataset.default) country.value = country.dataset.default;
      function update(initial = false) {
        const provinces = JSON.parse(
          country.selectedOptions[0]?.dataset.provinces || "[]",
        );
        province.replaceChildren(
          ...provinces.map(([value, label]) => new Option(label, value)),
        );
        container.hidden = provinces.length === 0;
        province.disabled = provinces.length === 0;
        if (initial && province.dataset.default)
          province.value = province.dataset.default;
      }
      country.addEventListener("change", () => update());
      update(true);
    });
  }
  function init(root = document) {
    root.querySelectorAll("[data-product-section]").forEach(initProduct);
    initAddresses(root);
    initModels(root);
    root.querySelectorAll("form .errors").forEach((error) => {
      const details = error.closest("details");
      if (details) details.open = true;
    });
  }
  document.addEventListener("click", async (event) => {
    const toggle = event.target.closest("[data-menu-toggle]");
    if (toggle) {
      const menu = document.getElementById(
        toggle.getAttribute("aria-controls"),
      );
      menu.hidden = !menu.hidden;
      toggle.setAttribute("aria-expanded", String(!menu.hidden));
    }
    const media = event.target.closest("[data-media-target]");
    if (media)
      selectMedia(
        media.closest("[data-product-section]"),
        media.dataset.mediaTarget,
      );
    if (event.target.closest("[data-close-cart]")) closeDrawer();
    if (event.target === drawer()) {
      const bounds = drawer().getBoundingClientRect();
      if (
        event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom
      )
        closeDrawer();
    }
    const open = event.target.closest("[data-open-cart]");
    if (open && config.drawer && drawer()?.showModal) {
      event.preventDefault();
      showDrawer();
      if (!cartBusy) {
        setCartBusy(true);
        try {
          await refreshCart();
        } catch (failure) {
          drawer().querySelector("[data-drawer-error]").textContent =
            failure.message;
        } finally {
          setCartBusy(false);
        }
      }
    }
    const remove = event.target.closest("[data-cart-remove]");
    if (remove?.closest("#CartDrawer")) {
      event.preventDefault();
      await changeLine(remove.dataset.cartRemove, 0);
    }
    if (event.target.closest("[data-print]")) window.print();
  });
  document.addEventListener("change", (event) => {
    if (event.target.matches("#CartDrawer [data-cart-key]"))
      changeLine(event.target.dataset.cartKey, Number(event.target.value));
  });
  document.addEventListener("submit", async (event) => {
    const form = event.target;
    if (
      form.matches("[data-delete-address]") &&
      !window.confirm("Delete this address?")
    )
      event.preventDefault();
    if (!form.matches("[data-product-form]")) return;
    event.preventDefault();
    const button = form.querySelector("[data-add-button]");
    const error = form.querySelector("[data-form-error]");
    if (cartBusy) {
      error.textContent = "Please wait for the current cart update.";
      return;
    }
    if (button.disabled || !form.reportValidity()) return;
    const original = button.textContent;
    const data = new FormData(form);
    // Only Shopify product form fields are submitted; radios are UI state, not line properties.
    [...data.keys()]
      .filter((key) => key.startsWith("option-"))
      .forEach((key) => data.delete(key));
    if (!data.get("selling_plan")) data.delete("selling_plan");
    button.disabled = true;
    button.textContent = "Adding…";
    error.textContent = "";
    form.setAttribute("aria-busy", "true");
    setCartBusy(true);
    let added = false;
    try {
      await request(`${config.add}.js`, { method: "POST", body: data });
      added = true;
      if (!config.drawer) {
        location.assign(config.cart);
        return;
      }
      await refreshCart();
      showDrawer();
      announce("Added to your vault.");
    } catch (failure) {
      if (added) {
        location.assign(config.cart);
        return;
      } // Never repeat an add after a successful mutation.
      error.textContent = failure.message;
    } finally {
      button.disabled = false;
      button.textContent = original;
      form.removeAttribute("aria-busy");
      setCartBusy(false);
      // A variant might have changed while the request was in flight.
      form.dispatchEvent(new Event("vault:variant-sync"));
    }
  });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    document.querySelectorAll("[data-menu-toggle]").forEach((toggle) => {
      const menu = document.getElementById(
        toggle.getAttribute("aria-controls"),
      );
      if (menu && !menu.hidden) {
        menu.hidden = true;
        toggle.setAttribute("aria-expanded", "false");
        toggle.focus();
      }
    });
  });
  document.addEventListener("shopify:section:load", (event) =>
    init(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    event.target
      .querySelectorAll("model-viewer")
      .forEach((model) => model.vaultUI?.destroy?.());
  });
  init();
  window.addEventListener("load", () => initModels(), { once: true });
})();
